import React, { useEffect, useState } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useResponsive } from '@/hooks/useResponsive';
import { cn } from '@/lib/utils';

interface ResponsiveChartProps {
  /**
   * Dados genéricos para o gráfico. As chaves de cada objeto devem corresponder a dataKey e xAxisKey.
   */
  data: unknown[];
  title?: string;
  type?: 'bar' | 'line' | 'area' | 'pie';
  dataKey?: string;
  xAxisKey?: string;
  height?: number;
  colors?: string[];
  className?: string;
  showLegend?: boolean;
  showGrid?: boolean;
}

export const ResponsiveChart: React.FC<ResponsiveChartProps> = ({
  data,
  title,
  type = 'bar',
  dataKey = 'value',
  xAxisKey = 'name',
  height = 300,
  colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1'],
  className = "",
  showLegend = true,
  showGrid = true
}) => {
  const { isMobile, width } = useResponsive();
  const [containerWidth, setContainerWidth] = useState(width);

  useEffect(() => {
    setContainerWidth(width);
  }, [width]);

  // Configurações responsivas
  const fontSize = isMobile ? 10 : 12;
  const tickAngle = isMobile ? -45 : 0;
  const chartHeight = isMobile ? 250 : height;

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return (
          <BarChart data={data}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis 
              dataKey={xAxisKey}
              fontSize={fontSize}
              angle={tickAngle}
              textAnchor={tickAngle !== 0 ? 'end' : 'middle'}
              height={isMobile ? 60 : 40}
              interval={isMobile ? 'preserveStartEnd' : 0}
            />
            <YAxis fontSize={fontSize} />
            <Tooltip 
              contentStyle={{
                fontSize: fontSize,
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                backgroundColor: 'white'
              }}
            />
            <Bar dataKey={dataKey} fill={colors[0]} radius={[4, 4, 0, 0]} />
          </BarChart>
        );

      case 'line':
        return (
          <LineChart data={data}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis 
              dataKey={xAxisKey}
              fontSize={fontSize}
              angle={tickAngle}
              textAnchor={tickAngle !== 0 ? 'end' : 'middle'}
              height={isMobile ? 60 : 40}
            />
            <YAxis fontSize={fontSize} />
            <Tooltip 
              contentStyle={{
                fontSize: fontSize,
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                backgroundColor: 'white'
              }}
            />
            <Line 
              type="monotone" 
              dataKey={dataKey} 
              stroke={colors[0]} 
              strokeWidth={2}
              dot={{ r: 4 }}
            />
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart data={data}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis 
              dataKey={xAxisKey}
              fontSize={fontSize}
              angle={tickAngle}
              textAnchor={tickAngle !== 0 ? 'end' : 'middle'}
              height={isMobile ? 60 : 40}
            />
            <YAxis fontSize={fontSize} />
            <Tooltip 
              contentStyle={{
                fontSize: fontSize,
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                backgroundColor: 'white'
              }}
            />
            <Area 
              type="monotone" 
              dataKey={dataKey} 
              stroke={colors[0]} 
              fill={colors[0]}
              fillOpacity={0.3}
            />
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              outerRadius={isMobile ? 60 : 80}
              fill="#8884d8"
              dataKey={dataKey}
              label={!isMobile}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{
                fontSize: fontSize,
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                backgroundColor: 'white'
              }}
            />
          </PieChart>
        );

      default:
        return null;
    }
  };

  return (
    <Card className={cn("w-full", className)}>
      {title && (
        <CardHeader className="pb-2">
          <CardTitle className={cn(
            isMobile ? "text-base" : "text-lg"
          )}>
            {title}
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className={cn(title ? "pt-0" : "")}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          {renderChart()}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Componente para métricas responsivas
interface MetricCardProps {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon?: React.ElementType;
  bg?: string;
  className?: string;
}

export const ResponsiveMetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  changeType = 'neutral',
  icon: Icon,
  bg = "bg-gradient-to-r from-blue-500 to-cyan-500",
  className = ""
}) => {
  const { isMobile } = useResponsive();

  const getChangeColor = () => {
    switch (changeType) {
      case 'positive': return 'text-green-600';
      case 'negative': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <Card className={cn("overflow-hidden h-full", className)}>
      <CardContent className="p-0 h-full">
        <div className={cn("p-4 text-white h-full", bg)}>
          <div className="flex items-center justify-between h-full">
            <div className="flex-1 min-w-0">
              <p className={cn(
                "text-white/80 font-medium truncate",
                isMobile ? "text-xs" : "text-sm"
              )}>
                {title}
              </p>
              <p className={cn(
                "text-white font-bold mt-1",
                isMobile ? "text-lg" : "text-xl"
              )}>
                {value}
              </p>
              {change && (
                <p className={cn(
                  "text-white/80 mt-1 truncate",
                  isMobile ? "text-xs" : "text-sm"
                )}>
                  {change}
                </p>
              )}
            </div>
            {Icon && (
              <div className="ml-2 flex-shrink-0">
                <Icon className={cn(
                  "text-white/80",
                  isMobile ? "w-5 h-5" : "w-6 h-6"
                )} />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Grid responsivo para métricas
interface ResponsiveMetricsGridProps {
  metrics: MetricCardProps[];
  className?: string;
}

export const ResponsiveMetricsGrid: React.FC<ResponsiveMetricsGridProps> = ({
  metrics,
  className = ""
}) => {
  const { isMobile } = useResponsive();

  // Para 5 cards, ajustar o layout para distribuir melhor
  const getGridLayout = () => {
    if (metrics.length === 5) {
      return isMobile 
        ? "grid-cols-1 sm:grid-cols-2" 
        : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5";
    }
    return isMobile 
      ? "grid-cols-1 sm:grid-cols-2" 
      : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4";
  };

  return (
    <div className={cn(
      "grid gap-4 mb-6 items-stretch",
      getGridLayout(),
      className
    )}>
      {metrics.map((metric, index) => (
        <ResponsiveMetricCard key={index} {...metric} />
      ))}
    </div>
  );
};

// Container para dashboard responsivo
interface ResponsiveDashboardProps {
  children: React.ReactNode;
  className?: string;
}

export const ResponsiveDashboard: React.FC<ResponsiveDashboardProps> = ({
  children,
  className = ""
}) => {
  const { isMobile } = useResponsive();

  return (
    <div className={cn(
      "space-y-6",
      isMobile ? "p-4" : "p-6",
      className
    )}>
      {children}
    </div>
  );
};

// Grid responsivo para gráficos
interface ResponsiveChartsGridProps {
  children: React.ReactNode;
  columns?: 1 | 2 | 3;
  className?: string;
}

export const ResponsiveChartsGrid: React.FC<ResponsiveChartsGridProps> = ({
  children,
  columns = 2,
  className = ""
}) => {
  const { isMobile } = useResponsive();

  const getGridCols = () => {
    if (isMobile) return "grid-cols-1";
    
    switch (columns) {
      case 1: return "grid-cols-1";
      case 2: return "grid-cols-1 lg:grid-cols-2";
      case 3: return "grid-cols-1 md:grid-cols-2 xl:grid-cols-3";
      default: return "grid-cols-1 lg:grid-cols-2";
    }
  };

  return (
    <div className={cn(
      "grid gap-6",
      getGridCols(),
      className
    )}>
      {children}
    </div>
  );
}; 